import { yupResolver } from '@hookform/resolvers/yup';
import { Box, SvgIcon, Typography } from '@mui/material';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { Button, TextFieldWrapper } from '@Components/index';
import Modal from '@Components/Modal';
import Icons from '@Constants/Icons.constants';
import {
  useSendPinForNumberSharingMutation,
  useSendSmsForNumberSharingMutation,
} from '@Services/userCallerIds.service';
import { BaseAlertProps } from '@Typings/global.types';
import useAlert from '@Utils/hooks/useAlert.hook';

import {
  NumberSharingModalInputs,
  NumberSharingModalProps,
} from './NumberSharingModal.types';

const NumberSharingModal: FunctionComponent<NumberSharingModalProps> = (
  props: NumberSharingModalProps
): JSX.Element => {
  const {
    isOpen,
    setIsOpen,
    numberToShare,
    setIsCallerIdInvalidated,
    voiceProvider,
  } = props;

  const { t } = useTranslation();
  const { triggerAlert } = useAlert();

  const [requestId, setRequestId] = useState<string>('');
  const [isPinRetriesExceeded, setIsPinRetriesExceeded] =
    useState<boolean>(false);
  const [isWrongPin, setIsWrongPin] = useState<boolean>(false);
  const [isSMSRetriesExceeded, setIsSMSRetriesExceeded] =
    useState<boolean>(false);
  const [isSuccessfullyValidated, setIsSuccessfullyValidated] =
    useState<boolean>(false);
  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });

  const [sendSmsForSharing, sendSmsForSharingMethods] =
    useSendSmsForNumberSharingMutation();

  const [numberSharingValidation, numberSharingValidationMethods] =
    useSendPinForNumberSharingMutation();

  const isLoading =
    sendSmsForSharingMethods.isLoading ||
    numberSharingValidationMethods.isLoading;

  const pinValidationSchema = (): yup.AnyObjectSchema => {
    const requiredMessage = t('common.formErrorMessages.required');
    const lengthMessage = t('common.formErrorMessages.pin_length_error');

    return yup
      .object({
        pin: yup.string().required(requiredMessage).length(6, lengthMessage),
      })
      .required();
  };

  const methods = useForm<NumberSharingModalInputs>({
    resolver: yupResolver(pinValidationSchema()),
    mode: 'onChange',
    shouldUnregister: true,
  });

  /**
   * Sends pin for validation
   */
  const onSubmitHandler: SubmitHandler<NumberSharingModalInputs> = (
    formData
  ) => {
    if (formData) {
      setIsPinRetriesExceeded(false);

      return numberSharingValidation({
        id: requestId,
        pin: formData.pin,
        voiceProvider,
      });
    }
  };

  /**
   * Error and Success alerts for pin validation
   */
  useEffect(() => {
    if (numberSharingValidationMethods.error) {
      triggerAlert(numberSharingValidationMethods.error, setAlertState);
    }
    if (numberSharingValidationMethods.isSuccess) {
      setIsWrongPin(false);

      if (
        !numberSharingValidationMethods.data.success &&
        numberSharingValidationMethods.data.message === 'PIN_RETRIES_EXCEEDED'
      ) {
        setIsPinRetriesExceeded(true);
      } else if (
        !numberSharingValidationMethods.data.success &&
        numberSharingValidationMethods.data.message === 'WRONG_PIN'
      ) {
        setIsWrongPin(true);
      } else if (
        numberSharingValidationMethods.data.success &&
        numberSharingValidationMethods.data.message === 'SUCCESSFULLY_VALIDATED'
      ) {
        setIsSuccessfullyValidated(true);
        setIsSMSRetriesExceeded(false);
        setIsPinRetriesExceeded(false);
        setRequestId('');
      }
    }
  }, [
    numberSharingValidationMethods.isSuccess,
    numberSharingValidationMethods.error,
    numberSharingValidationMethods.data,
    triggerAlert,
    numberSharingValidationMethods,
    setIsOpen,
  ]);

  /**
   * Error and Success alerts for sending sms with pin
   */
  useEffect(() => {
    if (sendSmsForSharingMethods.error) {
      triggerAlert(sendSmsForSharingMethods.error, setAlertState);
    }
    if (sendSmsForSharingMethods.isSuccess) {
      setIsWrongPin(false);
      if (sendSmsForSharingMethods.data.message === 'SMS_AMOUNT_EXCEEDED') {
        setRequestId('');
        setIsSMSRetriesExceeded(true);
      } else {
        setRequestId(sendSmsForSharingMethods.data.phoneSharingId);
      }
    }
  }, [
    sendSmsForSharingMethods.isSuccess,
    sendSmsForSharingMethods.error,
    sendSmsForSharingMethods.data,
    triggerAlert,
    sendSmsForSharingMethods,
  ]);

  const sendSms = () => {
    setIsPinRetriesExceeded(false);
    return sendSmsForSharing({ phone: numberToShare });
  };

  const closeModal = () => {
    if (isSuccessfullyValidated) {
      setIsCallerIdInvalidated(true);
    }

    setIsOpen(false);
    setRequestId('');
    setIsPinRetriesExceeded(false);
    setIsSMSRetriesExceeded(false);
    setIsSuccessfullyValidated(false);
  };

  return (
    <>
      <Modal
        open={isOpen}
        headerText={t('userCallerIds.Number Sharing')}
        onClose={() => closeModal()}
        alertState={alertState}
      >
        <FormProvider {...methods}>
          <Box
            component="form"
            onSubmit={methods.handleSubmit(onSubmitHandler)}
          >
            <>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                sx={{ padding: '3rem 2.4rem 2.4rem' }}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  mt={3}
                >
                  {isSuccessfullyValidated && (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      mb={4}
                    >
                      <SvgIcon
                        sx={{
                          width: 33.3,
                          height: 33.3,
                          mb: 1.4,
                          color: '#178549',
                        }}
                      >
                        {Icons.checkCircle}
                      </SvgIcon>
                      <Typography fontSize="18px" fontWeight="600">
                        {`${t('userCallerIds.Successfully Shared')}!`}
                      </Typography>
                    </Box>
                  )}
                  {!requestId &&
                    !isSuccessfullyValidated &&
                    !isSMSRetriesExceeded && (
                      <>
                        <Typography variant="h2" mb={1}>
                          <Trans
                            values={{ numberToShare }}
                            i18nKey="userCallerIds.Share Number"
                          />
                        </Typography>
                        <Box px={11.5}>
                          <Typography variant="main" textAlign="center">
                            {t('userCallerIds.Share Number Message')}
                          </Typography>
                        </Box>
                        <Box px={5} mt={3}>
                          <Button onClick={() => sendSms()} color="primary">
                            {t('common.messages.Send SMS')}
                          </Button>
                        </Box>
                      </>
                    )}
                  {(requestId || isSMSRetriesExceeded) &&
                    !isSuccessfullyValidated && (
                      <>
                        <Box mb={3}>
                          {isPinRetriesExceeded && (
                            <Typography
                              variant="h2"
                              fontWeight={500}
                              textAlign="center"
                              sx={{ color: '#a90000' }}
                            >
                              {t('userCallerIds.Pin Retries Exceeded')}
                            </Typography>
                          )}
                          {isSMSRetriesExceeded && (
                            <Typography
                              variant="h2"
                              fontWeight={500}
                              textAlign="center"
                              sx={{ color: '#a90000' }}
                            >
                              {t('userCallerIds.SMS Retries Exceeded')}
                            </Typography>
                          )}
                          {isWrongPin && (
                            <Typography
                              variant="h2"
                              fontWeight={500}
                              textAlign="center"
                              sx={{ color: '#a90000' }}
                            >
                              {t('userCallerIds.Wrong Pin')}
                            </Typography>
                          )}
                          {!isPinRetriesExceeded &&
                            !isWrongPin &&
                            !isSMSRetriesExceeded && (
                              <Typography variant="h2" textAlign="center">
                                {t('userCallerIds.Share Number Pin Message')}
                              </Typography>
                            )}
                        </Box>
                        <Box display="flex" gap={3}>
                          <TextFieldWrapper
                            name="pin"
                            placeholder={`${t('userCallerIds.Enter pin')} *`}
                            disabled={
                              isPinRetriesExceeded || isSMSRetriesExceeded
                            }
                          />
                          <Button
                            color="primary"
                            disabled={isLoading || isSMSRetriesExceeded}
                            onClick={() => sendSms()}
                          >
                            {t('common.messages.Resend SMS')}
                          </Button>
                        </Box>
                      </>
                    )}
                </Box>
              </Box>
              {(requestId || isSMSRetriesExceeded) && !isSuccessfullyValidated && (
                <Box
                  display="flex"
                  flexDirection="column"
                  sx={{
                    borderTop: '0.1rem solid #A3ABB2',
                    padding: '3rem 2.4rem 2.4rem',
                  }}
                >
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      onClick={() => closeModal()}
                      color="secondary"
                      variant="outlined"
                      sx={{ ml: 'auto', mr: 1.6 }}
                    >
                      {t('common.messages.Cancel')}
                    </Button>
                    <Button
                      type="submit"
                      color="primary"
                      disabled={
                        isLoading ||
                        isSMSRetriesExceeded ||
                        isPinRetriesExceeded
                      }
                    >
                      {t('common.messages.Submit')}
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          </Box>
        </FormProvider>
      </Modal>
    </>
  );
};

export default NumberSharingModal;
